import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApiTimeslot } from '../../../common/api/timeslots/timeslotModels';

import { isProductionEnvironment } from '../../../../utility/environments';
import { willTimePassIn } from '../../../cnc/components/timeslotSelector/timeslotUtils';
import { useTimeslotsForCurrentContext } from '../../../cnc/hooks/useTimeslotsForCurrentContext';
import { useAppSelector } from '../../../common/hooks/useThunkDispatch';
import { useTimeExpiration } from '../../../common/hooks/useTimeExpiration';
import { cartSelectors } from '../../../common/selectors/cartSelectors';
import { debugSelectors } from '../../../common/selectors/debugSelectors';

export const useEarliestTimeslotFetch = () => {
    const { data, isSuccess } = useTimeslotsForCurrentContext();

    const earliestTimeslot = useMemo(() => {
        if (isSuccess && data?.length) {
            const earliestAvailableTimeslot = data.find((item: ApiTimeslot) => !item.fullyBooked);
            if (earliestAvailableTimeslot) {
                return earliestAvailableTimeslot;
            }
        }
        return null;
    }, [isSuccess, data]);

    return earliestTimeslot;
};

const MS_BEFORE_MAX_ORDER_TIME_PASSES = 1000 * 60 * 60; // 60 minutes
export const useMaxOrderTimeslotExpiration = () => {
    const currentTimeslot = useAppSelector(cartSelectors.selectedTimeslot);
    const editOrderExpirationTime = useAppSelector(cartSelectors.editOrderExpirationTime);
    const isInEditOrderMode = useAppSelector(cartSelectors.isInEditOrder);
    const [expireTimerTriggered, setExpireTimerTriggered] = useState(false);
    const { mockCartIsExpired, mockCartIsExpiringSoon } = useAppSelector(
        debugSelectors.selectDebug,
    );

    const [maxOrderTime, setMaxOrderTime] = useState(currentTimeslot?.maxOrderTime);

    useEffect(() => {
        if (isInEditOrderMode) {
            if (editOrderExpirationTime) {
                setMaxOrderTime(editOrderExpirationTime.valueOf());
            }
        } else if (currentTimeslot?.maxOrderTime) {
            setMaxOrderTime(currentTimeslot.maxOrderTime);
        }
    }, [currentTimeslot?.maxOrderTime, isInEditOrderMode, editOrderExpirationTime]);

    useEffect(() => {
        if ((mockCartIsExpired || mockCartIsExpiringSoon) && !isProductionEnvironment) {
            if (mockCartIsExpired) {
                setMaxOrderTime(new Date().valueOf() + 1000 * 60 * 1); // this shows notice instantly when you load page, since it sets maxOrderTime in 1 minute, for testing purposes only
                setShow(true);
            } else if (mockCartIsExpiringSoon) {
                setMaxOrderTime(new Date().valueOf() + MS_BEFORE_MAX_ORDER_TIME_PASSES + 1000 * 15); // Shows expiration after 15 secounds, for testing purposes only
            }
        }
    }, [mockCartIsExpired, mockCartIsExpiringSoon, setMaxOrderTime]);

    const [show, setShow] = useState(
        !!maxOrderTime && willTimePassIn(new Date(maxOrderTime), MS_BEFORE_MAX_ORDER_TIME_PASSES),
    );

    const nowMs = new Date().valueOf();
    const milisecondsToShowNotice = maxOrderTime
        ? maxOrderTime - nowMs - MS_BEFORE_MAX_ORDER_TIME_PASSES
        : null;

    const showExpieryTime = useCallback(() => {
        setShow(true);
        setExpireTimerTriggered(true);
    }, [setShow]);
    // enable "show" counter only when notice is hidden
    useTimeExpiration(!show ? milisecondsToShowNotice : null, showExpieryTime, !maxOrderTime);
    return { show, maxOrderTime, expireTimerTriggered };
};
